import { useCallback, useEffect, useState } from 'react';
import { Button, Callout, Checkbox, Spinner, Tag, TextField, type SelectFieldOption } from '@humanforce/mango-ui-react';
import { SectionShell } from '../components/SectionShell';
import { MangoSelectField } from '../components/MangoSelectField';
import { RetroactiveReasonField } from '../components/RetroactiveReasonField';
import { WorkerDrawer } from '../components/WorkerDrawer';
import { addContact, editContact, listContacts, removeContact, type ReferenceData } from '../workersApi';
import { CONTACT_TYPE_LABELS, type ContactType, type PersonContactResponse } from '../workersApiTypes';
import { hasErrors, isRetroactive, validateContact, type FieldErrors } from '../workersValidation';
import { trimOrUndefined } from '../workersUtils';

interface ContactsSectionProps {
  personId: string;
  reference: ReferenceData | null;
  readOnly: boolean;
}

interface ContactDraftState {
  contactType: string;
  contactValue: string;
  isPrimary: boolean;
  effectiveFrom: string;
  retroactiveReason: string;
}

const EMPTY: ContactDraftState = { contactType: '', contactValue: '', isPrimary: false, effectiveFrom: '', retroactiveReason: '' };
const typeLabel = (t?: string) => CONTACT_TYPE_LABELS[t as ContactType] ?? t ?? '—';

export const ContactsSection = ({ personId, reference, readOnly }: ContactsSectionProps) => {
  const [contacts, setContacts] = useState<PersonContactResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PersonContactResponse | null>(null);
  const [draft, setDraft] = useState<ContactDraftState>(EMPTY);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>();

  const reload = useCallback(async (signal?: { ignore: boolean }) => {
    setLoading(true);
    const result = await listContacts(personId);
    if (signal?.ignore) return;
    if (result.ok) setContacts(result.items);
    else setLoadError(result.message ?? 'Failed to load contacts.');
    setLoading(false);
  }, [personId]);

  useEffect(() => {
    const signal = { ignore: false };
    void reload(signal);
    return () => { signal.ignore = true; };
  }, [reload]);

  const contactTypeOptions: SelectFieldOption[] = (reference?.contactTypes ?? []).map((t) => ({ label: typeLabel(t), value: t }));

  const openAdd = () => { setEditing(null); setDraft(EMPTY); setErrors({}); setSaveError(undefined); setOpen(true); };
  const openEdit = (contact: PersonContactResponse) => {
    setEditing(contact);
    setDraft({ contactType: contact.contactType ?? '', contactValue: contact.contactValue ?? '', isPrimary: Boolean(contact.isPrimary), effectiveFrom: '', retroactiveReason: '' });
    setErrors({});
    setSaveError(undefined);
    setOpen(true);
  };
  const set = <K extends keyof ContactDraftState>(key: K) => (value: ContactDraftState[K]) => setDraft((d) => ({ ...d, [key]: value }));

  const showRetro = isRetroactive(draft.effectiveFrom || undefined);

  const save = async () => {
    setSaveError(undefined);
    const validation = validateContact({ contactType: draft.contactType, contactValue: draft.contactValue, effectiveFrom: draft.effectiveFrom || undefined, retroactiveReason: draft.retroactiveReason || undefined });
    setErrors(validation);
    if (hasErrors(validation)) return;

    setSaving(true);
    const result = editing
      ? await editContact(personId, editing.contactId ?? '', { contactValue: draft.contactValue.trim(), isPrimary: draft.isPrimary, effectiveFrom: trimOrUndefined(draft.effectiveFrom), retroactiveReason: trimOrUndefined(draft.retroactiveReason) })
      : await addContact(personId, { contactType: draft.contactType as ContactType, contactValue: draft.contactValue.trim(), isPrimary: draft.isPrimary, effectiveFrom: trimOrUndefined(draft.effectiveFrom), retroactiveReason: trimOrUndefined(draft.retroactiveReason) });
    setSaving(false);
    if (result.ok) { setOpen(false); await reload(); }
    else setSaveError(result.message ?? 'Could not save the contact.');
  };

  const onDelete = async (contact: PersonContactResponse) => {
    setActionError(null);
    const result = await removeContact(personId, contact.contactId ?? '');
    if (result.ok) await reload();
    else setActionError(result.message ?? 'Could not remove the contact.');
  };

  return (
    <SectionShell
      title="Contacts"
      description="Email and phone contacts. At least one email must be kept."
      action={!readOnly && <Button variant="secondary" label="Add contact" icon="add" onClick={openAdd} />}
    >
      {loadError && <Callout tone="critical" title="Couldn't load contacts">{loadError}</Callout>}
      {actionError && <Callout tone="critical" title="Action failed">{actionError}</Callout>}

      {loading ? (
        <div className="mgo-cluster mgo-gap-s mgo-align-center"><Spinner /> <span className="mgo-text-s mgo-color-muted">Loading contacts…</span></div>
      ) : contacts.length === 0 ? (
        <p className="mgo-text-s mgo-color-muted" style={{ margin: 0 }}>No contacts yet.</p>
      ) : (
        <div className="mgo-stack mgo-gap-s">
          {contacts.map((contact) => (
            <div key={contact.contactId} className="mgo-split mgo-align-center mgo-border-neutral mgo-radius-normal" style={{ padding: 'var(--mgo-space-30)' }}>
              <div className="mgo-stack mgo-gap-2xs" style={{ minWidth: 0 }}>
                <div className="mgo-cluster mgo-gap-s mgo-align-center">
                  <span className="mgo-text-s mgo-weight-strong">{contact.contactValue}</span>
                  {contact.isPrimary && <Tag label="Primary" tone="blue" weight="strong" />}
                  {contact.verified && <Tag label="Verified" tone="positive" />}
                </div>
                <span className="mgo-text-xs mgo-color-muted">{typeLabel(contact.contactType)}</span>
              </div>
              {!readOnly && (
                <div className="mgo-cluster mgo-gap-s" style={{ flexShrink: 0 }}>
                  <Button variant="tertiary" size="small" label="Edit" onClick={() => openEdit(contact)} />
                  <Button variant="tertiary" size="small" label="Remove" onClick={() => onDelete(contact)} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <WorkerDrawer
        open={open}
        title={editing ? 'Edit contact' : 'Add contact'}
        subtitle={editing ? 'Update this contact (creates a new effective version).' : 'Add a new contact for this person.'}
        onClose={() => setOpen(false)}
        onSave={save}
        saving={saving}
        error={saveError}
      >
        <div className="mgo-stack mgo-gap-l">
          <MangoSelectField label="Contact type" required value={draft.contactType} options={contactTypeOptions} onChange={set('contactType')} disabled={Boolean(editing)} error={errors.contactType} />
          <TextField label="Contact value" required placeholder="email@example.com or +61400000000" value={draft.contactValue} onChange={(e) => set('contactValue')(e.target.value)} status={errors.contactValue ? 'critical' : undefined} statusText={errors.contactValue} />
          <Checkbox id="contact-is-primary" label="Primary for this type" checked={draft.isPrimary} onChange={(checked) => set('isPrimary')(checked)} helpText="Marking primary demotes any existing primary of the same type." />
          <TextField label="Effective from" type="date" value={draft.effectiveFrom} onChange={(e) => set('effectiveFrom')(e.target.value)} helpText="Leave blank to take effect now." />
          <RetroactiveReasonField show={showRetro} value={draft.retroactiveReason} onChange={set('retroactiveReason')} error={errors.retroactiveReason} />
        </div>
      </WorkerDrawer>
    </SectionShell>
  );
};
